"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Car, Wifi, Music, Snowflake, Shield, Smartphone } from "lucide-react";
export default function VehicleFeatures() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const amenities = [{
    icon: Car,
    title: "Premium Leather Seating",
    description: "Handcrafted Italian leather with climate control"
  }, {
    icon: Snowflake,
    title: "Dual-Zone Climate",
    description: "Individual temperature settings for ultimate comfort"
  }, {
    icon: Music,
    title: "Premium Sound System",
    description: "Concert-quality audio with noise cancellation"
  }, {
    icon: Wifi,
    title: "High-Speed WiFi",
    description: "Stay connected throughout your journey"
  }];
  const techFeatures = [{
    icon: Smartphone,
    title: "Device Integration",
    description: "Seamless connectivity with Apple CarPlay & Android Auto"
  }, {
    icon: Shield,
    title: "Advanced Safety",
    description: "State-of-the-art collision avoidance systems"
  }, {
    icon: Car,
    title: "Ambient Lighting",
    description: "Customizable interior mood lighting"
  }];
  return <section id="vehicle-features" className="dark bg-background text-foreground relative min-h-screen overflow-hidden py-24 md:py-32">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-fixed opacity-10 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/cadillac_escalade_luxury.png)]" />

      {}
      <div className="relative z-10 container mx-auto px-4 max-w-7xl">
        {}
        <div ref={titleRef} className={`text-center mb-16 transition-all duration-[var(--duration-slow)] ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-foreground">
            Vehicle Features
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Experience luxury travel with our premium fleet equipped with the
            finest amenities and cutting-edge technology
          </p>
        </div>

        {}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {}
          <Card ref={card1Ref} data-index={0} className={`glass-effect border-white/10 transition-all duration-[var(--duration-slow)] ${card1Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-2xl md:text-3xl font-display text-foreground">
                Premium Amenities
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {amenities.map((amenity, i) => {
              const Icon = amenity.icon;
              return <div key={i} data-index={i} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] ${card1Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`} style={{
                transitionDelay: card1Visible ? `${i * 100}ms` : "0ms"
              }}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center shadow-[0_0_12px_rgba(45,103,196,0.3)]">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {amenity.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {amenity.description}
                      </p>
                    </div>
                  </div>;
            })}
            </CardContent>
          </Card>

          {}
          <Card ref={card2Ref} data-index={1} className={`glass-effect border-white/10 transition-all duration-[var(--duration-slow)] ${card2Visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-2xl md:text-3xl font-display text-foreground">
                Advanced Technology
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {techFeatures.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={i} data-index={i} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] ${card2Visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`} style={{
                transitionDelay: card2Visible ? `${i * 100}ms` : "0ms"
              }}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-md bg-secondary/20 flex items-center justify-center shadow-[0_0_12px_rgba(218,165,32,0.3)]">
                      <Icon className="w-6 h-6 text-foreground" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </div>;
            })}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}